import {
  getPositionPnlRoi,
  normalizeOrderSide,
  parseOrderDate,
} from "@helpers/formatOrders";
import { getTriggerPnl, toNumber } from "../../Orders/positionFormat";
import { LEVEL_KIND, ZONE_KIND, ZONE_TOWARDS } from "./constants";

/**
 * @typedef {{ kind: string, price: number, percent: number }} PositionLevel
 * @typedef {{ kind: string, price: number, towards: string }} PositionZone
 * @typedef {{
 *   id: string|number,
 *   isLong: boolean,
 *   leverage: number|null,
 *   entry: { price: number, pnl: number|null, time: number|null },
 *   levels: PositionLevel[],
 *   zones: PositionZone[],
 * }} PositionLinesModel
 */

/**
 * Price from the API or null. Zero is a real level only for liquidation
 * (1x long liquidates at 0), missing TP/SL triggers come as null or 0.
 */
const toPrice = (value, { allowZero = false } = {}) => {
  const price = toNumber(value);
  if (price == null) return null;

  return price > 0 || (allowZero && price === 0) ? price : null;
};

/**
 * Result at the level as % of the wager: 40 for "+40%", -20 for "-20%".
 * Prices are stored on the position, so a partial close keeps both the
 * lines and the percents: quantity and margin shrink together.
 */
const getLevelPercent = ({ price, entryPrice, quantity, margin, isLong }) => {
  if (!margin) return null;

  const pnl = getTriggerPnl(price, entryPrice, quantity, !isLong);

  return pnl == null ? null : (pnl / margin) * 100;
};

const buildLevels = ({ position, entryPrice, quantity, margin, wager, isLong }) => {
  const takeProfit = toPrice(position.take_profit_trigger_price);
  const stopLoss = toPrice(position.stop_loss_trigger_price);
  const liquidation = toPrice(position.liq_price, { allowZero: true });

  const candidates = [
    { kind: LEVEL_KIND.TAKE_PROFIT, price: takeProfit },
    { kind: LEVEL_KIND.STOP_LOSS, price: stopLoss },
    // The stop loss closes the trade first, liquidation is shown only without it
    { kind: LEVEL_KIND.LIQUIDATION, price: stopLoss ? null : liquidation },
  ];

  return candidates
    .filter(({ price }) => price != null)
    .map(({ kind, price }) => ({
      kind,
      price,
      // TP/SL are set in percents of the wager, liquidation takes the whole margin
      percent: getLevelPercent({
        price,
        entryPrice,
        quantity,
        margin: kind === LEVEL_KIND.LIQUIDATION ? margin : wager,
        isLong,
      }),
    }));
};

/**
 * Profit zone starts at take profit, loss zone at the level that closes
 * a losing trade (stop loss or liquidation), both go away from the entry.
 */
const buildZones = (levels, isLong) =>
  levels.map(({ kind, price }) => {
    const isProfit = kind === LEVEL_KIND.TAKE_PROFIT;
    const goesUp = isProfit === isLong;

    return {
      kind: isProfit ? ZONE_KIND.PROFIT : ZONE_KIND.LOSS,
      price,
      towards: goesUp ? ZONE_TOWARDS.UP : ZONE_TOWARDS.DOWN,
    };
  });

/**
 * Chart model of one open position, free of the chart engine and texts.
 *
 * @param {object} position open position from /futures/positions/open
 * @param {number|null} markPrice live price for the PnL
 * @returns {PositionLinesModel|null} null when the position can't be drawn
 */
export const buildPositionLines = (position, markPrice) => {
  const entryPrice = toPrice(
    position?.entry_price ?? position?.fill_price ?? position?.price,
  );
  const { isBuySide, isSellSide } = normalizeOrderSide(position?.side);

  if (!entryPrice || (!isBuySide && !isSellSide)) return null;

  const isLong = isBuySide;
  const quantity = Math.abs(
    toNumber(position.quantity ?? position.qty) ?? 0,
  );
  const margin = toNumber(position.margin);
  const wager = margin == null ? null : margin + (toNumber(position.open_fee) ?? 0);
  const levels = buildLevels({
    position,
    entryPrice,
    quantity,
    margin,
    wager,
    isLong,
  });
  const { pnlValue } = getPositionPnlRoi(position, markPrice);

  return {
    id: position.id,
    isLong,
    leverage: toNumber(position.leverage),
    entry: {
      price: entryPrice,
      pnl: Number.isFinite(pnlValue) ? pnlValue : null,
      // Open time in ms for the entry marker; read like everywhere in the app (parseOrderDate)
      time: parseOrderDate(position.created_at)?.getTime() ?? null,
    },
    levels,
    zones: buildZones(levels, isLong),
  };
};
